import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import MainSidebar from 'components/ui/MainSidebar';
import MemberCard from './components/MemberCard';
import DirectMessagingPanel from './components/DirectMessagingPanel';
import { supabase } from 'lib/supabase';
import { useAuth } from 'contexts/AuthContext';


const EXPERIENCE_LEVELS = ['All', 'Beginner', 'Intermediate', 'Advanced'];
const INVESTMENT_FOCUSES = ['All', 'Real Estate', 'Stocks', 'Crypto', 'ETFs', 'Venture Capital', 'Options', 'DeFi'];

function mapProfile(p) {
  return {
    id: p?.id,
    fullName: p?.full_name,
    avatarUrl: p?.avatar_url,
    title: p?.title,
    location: p?.location,
    bio: p?.bio,
    experienceLevel: p?.experience_level,
    isFoundingMember: p?.is_founding_member,
    isMentor: p?.is_mentor,
    isWinVerified: p?.is_win_verified,
    investmentInterests: p?.investment_interests || [],
    followersCount: p?.followers_count || 0,
    isFollowing: p?.is_following || false,
  };
}

export default function MemberProfiles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [members, setMembers] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterExperience, setFilterExperience] = useState('All');
  const [filterFocus, setFilterFocus] = useState('All');
  const [filterMentor, setFilterMentor] = useState(false);
  const [messagingOpen, setMessagingOpen] = useState(false);
  const [messagingTarget, setMessagingTarget] = useState(null);
  const [myFollowing, setMyFollowing] = useState(new Set());

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.neq('id', user?.id || '00000000-0000-0000-0000-000000000000');

      if (filterExperience !== 'All') query = query?.eq('experience_level', filterExperience);
      if (filterMentor) query = query?.eq('is_mentor', true);

      const { data, error } = await query?.order('created_at', { ascending: false });
      if (error) throw error;

      // Get follow counts and following status
      const enriched = await Promise.all((data || [])?.map(async (p) => {
        const { count: followersCount } = await supabase?.from('member_follows')?.select('*', { count: 'exact', head: true })?.eq('following_id', p?.id);
        return { ...p, followers_count: followersCount || 0, is_following: myFollowing?.has(p?.id) };
      }));

      let filtered = enriched;
      if (searchQuery?.trim()) {
        const q = searchQuery?.toLowerCase();
        filtered = filtered?.filter(p =>
          p?.full_name?.toLowerCase()?.includes(q) ||
          p?.title?.toLowerCase()?.includes(q) ||
          p?.location?.toLowerCase()?.includes(q) ||
          p?.investment_interests?.some(i => i?.toLowerCase()?.includes(q))
        );
      }
      if (filterFocus !== 'All') {
        filtered = filtered?.filter(p => p?.investment_interests?.includes(filterFocus));
      }

      setMembers(filtered?.map(mapProfile));
      setMentors(filtered?.filter(p => p?.is_mentor)?.map(mapProfile));
    } catch (err) {
      console.error('Load members error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, filterExperience, filterMentor, filterFocus, searchQuery, myFollowing]);

  const loadMyFollowing = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase?.from('member_follows')?.select('following_id')?.eq('follower_id', user?.id);
    if (data) setMyFollowing(new Set(data.map(f => f.following_id)));
  }, [user?.id]);

  useEffect(() => {
    loadMyFollowing();
  }, [loadMyFollowing]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleFollow = async (memberId, shouldFollow) => {
    if (!user?.id) return;
    if (shouldFollow) {
      await supabase?.from('member_follows')?.insert({ follower_id: user?.id, following_id: memberId });
      setMyFollowing(prev => new Set([...prev, memberId]));
    } else {
      await supabase?.from('member_follows')?.delete()?.eq('follower_id', user?.id)?.eq('following_id', memberId);
      setMyFollowing(prev => { const s = new Set(prev); s?.delete(memberId); return s; });
    }
  };

  const handleMessage = (member) => {
    setMessagingTarget({
      id: member?.id,
      full_name: member?.fullName,
      avatar_url: member?.avatarUrl,
    });
    setMessagingOpen(true);
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--color-surface)' }}>
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main
        className={`flex-1 overflow-y-auto transition-all duration-250 pb-20 lg:pb-0 ${sidebarCollapsed ? 'lg:ml-[64px]' : 'lg:ml-[240px]'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2 pl-12 lg:pl-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--color-primary)' }}
              >
                <Icon name="Users" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-h2" style={{ color: 'var(--color-foreground)' }}>
                  WIN Members
                </h1>
                <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                  Connect, follow, and learn from fellow investors
                </p>
              </div>
            </div>
          </div>

          {/* Search + Filters */}
          <div className="bg-white rounded-xl border p-4 mb-6" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex flex-col lg:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name, interest, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-9 pr-4 h-10 rounded-lg border font-caption text-sm outline-none transition-colors"
                  style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', background: 'var(--color-surface)' }}
                />
              </div>
              {/* Experience filter */}
              <select
                value={filterExperience}
                onChange={(e) => setFilterExperience(e?.target?.value)}
                className="h-10 px-3 rounded-lg border font-caption text-sm outline-none"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', background: 'white' }}
              >
                {EXPERIENCE_LEVELS?.map(l => <option key={l} value={l}>{l === 'All' ? 'All Levels' : l}</option>)}
              </select>
              {/* Focus filter */}
              <select
                value={filterFocus}
                onChange={(e) => setFilterFocus(e?.target?.value)}
                className="h-10 px-3 rounded-lg border font-caption text-sm outline-none"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', background: 'white' }}
              >
                {INVESTMENT_FOCUSES?.map(f => <option key={f} value={f}>{f === 'All' ? 'All Focuses' : f}</option>)}
              </select>
              {/* Mentor toggle */}
              <button
                onClick={() => setFilterMentor(!filterMentor)}
                className="flex items-center gap-2 h-10 px-4 rounded-lg border font-caption text-sm transition-all"
                style={filterMentor
                  ? { background: 'var(--color-accent)', color: 'var(--color-primary)', borderColor: 'var(--color-accent)' }
                  : { borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)', background: 'white' }
                }
              >
                <Icon name="GraduationCap" size={15} color="currentColor" />
                Mentors Only
              </button>
            </div>
          </div>

          {/* Find a Mentor Section */}
          {mentors?.length > 0 && !filterMentor && filterExperience === 'All' && filterFocus === 'All' && !searchQuery && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Crown" size={18} color="var(--color-accent)" />
                <h2 className="font-heading font-semibold text-xl" style={{ color: 'var(--color-foreground)' }}>
                  Find a Mentor
                </h2>
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-caption font-medium"
                  style={{ background: 'rgba(133,113,77,0.12)', color: 'var(--color-accent)' }}
                >
                  {mentors?.length} Available
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {mentors?.slice(0, 4)?.map((mentor) => (
                  <div
                    key={mentor?.id}
                    onClick={() => navigate(`/members/${mentor?.id}`)}
                    className="bg-white rounded-xl border cursor-pointer hover:shadow-md transition-all duration-200 overflow-hidden"
                    style={{ borderColor: 'rgba(212,175,55,0.3)' }}
                  >
                    <div className="h-1" style={{ background: 'linear-gradient(90deg, var(--color-accent), #E8C96A)' }} />
                    <div className="p-4 flex items-center gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-12 h-12 rounded-full overflow-hidden border-2" style={{ borderColor: 'var(--color-accent)' }}>
                          <Image src={mentor?.avatarUrl} alt={`${mentor?.fullName} mentor profile`} className="w-full h-full object-cover" />
                        </div>
                        <div
                          className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ background: 'var(--color-accent)' }}
                        >
                          <Icon name="Crown" size={10} color="var(--color-primary)" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>{mentor?.fullName}</p>
                        <p className="font-caption text-xs truncate" style={{ color: 'var(--color-muted-foreground)' }}>{mentor?.title}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <Icon name="GraduationCap" size={11} color="var(--color-accent)" />
                          <span className="font-caption text-xs" style={{ color: 'var(--color-accent)' }}>Available as Mentor</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Members Grid */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-xl" style={{ color: 'var(--color-foreground)' }}>
              {filterMentor ? 'Available Mentors' : 'All Members'}
            </h2>
            <span className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
              {members?.length} member{members?.length !== 1 ? 's' : ''}
            </span>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)]?.map((_, i) => (
                <div key={i} className="bg-white rounded-xl border h-72 animate-pulse" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="h-1 rounded-t-xl" style={{ background: 'var(--color-muted)' }} />
                  <div className="p-5 space-y-3">
                    <div className="flex gap-3">
                      <div className="w-16 h-16 rounded-full" style={{ background: 'var(--color-muted)' }} />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 rounded" style={{ background: 'var(--color-muted)' }} />
                        <div className="h-3 rounded w-2/3" style={{ background: 'var(--color-muted)' }} />
                      </div>
                    </div>
                    <div className="h-3 rounded" style={{ background: 'var(--color-muted)' }} />
                    <div className="h-3 rounded w-4/5" style={{ background: 'var(--color-muted)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : members?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,48,87,0.08)' }}>
                <Icon name="Users" size={36} color="var(--color-primary)" />
              </div>
              <div className="text-center">
                <h3 className="font-heading font-semibold text-lg mb-1" style={{ color: 'var(--color-foreground)' }}>No members found</h3>
                <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>Try adjusting your search or filters</p>
              </div>
              <button
                onClick={() => { setSearchQuery(''); setFilterExperience('All'); setFilterFocus('All'); setFilterMentor(false); }}
                className="px-4 py-2 rounded-lg font-caption text-sm transition-all"
                style={{ background: 'var(--color-accent)', color: 'var(--color-primary)' }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {members?.map((member, index) => (
                <div key={member?.id} className={`card-enter card-enter-${Math.min(index + 1, 8)}`}>
                  <MemberCard
                    member={member}
                    onFollow={handleFollow}
                    onMessage={handleMessage}
                    currentUserId={user?.id}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <DirectMessagingPanel
        isOpen={messagingOpen}
        onClose={() => { setMessagingOpen(false); setMessagingTarget(null); }}
        initialMember={messagingTarget}
      />
    </div>
  );
}
